import type { IconName } from 'lucide-react/dynamic';
import { BaseApi } from './base';

export interface InventoryItem {
  _id: string;
  userId:
    | string
    | {
        _id: string;
        given_name: string;
        family_name: string;
      };
  item: {
    _id: string;
    title: string;
    description: string;
    price: number;
    icon: IconName;
  };
  amount: number;
  status: 'available' | 'pending_activation' | 'activated' | 'denied';
  processedBy?: {
    _id: string;
    given_name: string;
    family_name: string;
  };
  activationRequestedAt?: string;
  processedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ActivateItemRequest {
  inventoryItemId: string;
  count?: number;
}

export interface ProcessActivationRequest {
  action: 'approve' | 'deny';
}

export interface ManagerAddInventoryPayload {
  itemId: string;
  amount: number;
}

export interface ManagerUpdateInventoryPayload {
  amount: number;
}

export class InventoryApi extends BaseApi {
  /**
   * Get user's inventory
   */
  async getInventory(): Promise<InventoryItem[]> {
    return this._request<InventoryItem[]>('/inventory');
  }

  /**
   * Get specific inventory item
   */
  async getInventoryItem(inventoryItemId: string): Promise<InventoryItem> {
    return this._request<InventoryItem>(`/inventory/${inventoryItemId}`);
  }

  /**
   * Activate an inventory item
   */
  async activateItem(request: ActivateItemRequest): Promise<InventoryItem> {
    return this._request<InventoryItem>('/inventory/activate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
  }

  /**
   * Get pending activation requests (for managers)
   */
  async getPendingActivations(): Promise<InventoryItem[]> {
    return this._request<InventoryItem[]>('/inventory/activations/pending');
  }

  /**
   * Process activation request (for managers)
   */
  async processActivation(
    inventoryItemId: string,
    request: ProcessActivationRequest,
  ): Promise<InventoryItem> {
    return this._request<InventoryItem>(
      `/inventory/activations/${inventoryItemId}/process`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      },
    );
  }

  async getManagedUserInventory(userId: string): Promise<InventoryItem[]> {
    return this._request<InventoryItem[]>(`/users/${userId}/inventory`);
  }

  async addManagedUserInventoryItem(
    userId: string,
    payload: ManagerAddInventoryPayload,
  ): Promise<InventoryItem> {
    return this._request<InventoryItem>(`/users/${userId}/inventory`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  }

  async updateManagedUserInventoryItem(
    userId: string,
    inventoryItemId: string,
    payload: ManagerUpdateInventoryPayload,
  ): Promise<InventoryItem> {
    return this._request<InventoryItem>(
      `/users/${userId}/inventory/${inventoryItemId}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      },
    );
  }

  async removeManagedUserInventoryItem(
    userId: string,
    inventoryItemId: string,
  ): Promise<void> {
    await this._request<void>(`/users/${userId}/inventory/${inventoryItemId}`, {
      method: 'DELETE',
    });
  }
}

// This will be instantiated in the main API class
